import { useState } from 'react'
import { TimeChart } from '@/components/charts/time-chart'
import { KpiTile, Note, Panel, Tabs, Track } from '@/components/redesign/bits'
import { Skeleton } from '@/components/ui/skeleton'
import { ErrorState } from '@/components/ui/states'
import { PageTitle } from '@/app/layout'
import { numberFmt } from '@/lib/format'
import { type WebsiteDays, type WebsiteStats, useWebsiteStats } from './api'

const PERIODS: { key: `${WebsiteDays}`; label: string }[] = [
  { key: '1', label: 'Today' },
  { key: '7', label: '7 days' },
  { key: '30', label: '30 days' },
  { key: '90', label: '90 days' },
]

/** What each action is, in words. */
const ACTIONS: Record<string, string> = {
  start_free: 'Start free buttons',
  signup_click: 'Other sign-up links',
  free_audit_start: 'Free audits started',
  free_audit_done: 'Free audit results seen',
  free_audit_email_sent: 'Free audit report emailed',
  free_audit_exit_shown: 'Exit pop-up shown (free audit)',
  free_audit_exit_dismiss: 'Exit pop-up closed (free audit)',
  free_audit_signup: 'Create your free account (after a free audit)',
  get_plugin: 'Get the WordPress plugin',
  see_pricing: 'See pricing',
  watch_tour: 'Watch the tour',
  signin_click: 'Sign in',
}

const regions = new Intl.DisplayNames(['en'], { type: 'region' })
const countryName = (code: string) => {
  if (code === 'unknown') return 'Unknown'
  try {
    return regions.of(code) ?? code
  } catch {
    return code
  }
}
const percent = (part: number, whole: number) => (whole > 0 ? `${Math.round((part / whole) * 100)}%` : '–')

/**
 * arvaseo.com's anonymous visit counts (owner, 2026-10-24): every visitor, cookies accepted or
 * not, with no cookies, no IP addresses and each visitor counted once a day. Where visits come
 * from, which pages are read, and how many go on to a free audit, a sign-up click and an account.
 */
export function WebsitePage() {
  const [days, setDays] = useState<WebsiteDays>(30)
  const stats = useWebsiteStats(days)

  return (
    <>
      <PageTitle
        title="Website visitors"
        description="Anonymous counts for arvaseo.com from every visitor, whether or not they accept cookies: no cookies, no IP addresses, each visitor counted once a day. Google Analytics keeps the detail for people who accept it."
      />
      {stats.isPending ? (
        <Skeleton className="h-96 rounded-[14px]" />
      ) : stats.isError ? (
        <div className="card">
          <ErrorState error={stats.error} onRetry={() => void stats.refetch()} />
        </div>
      ) : (
        <Report stats={stats.data} days={days} onDays={setDays} />
      )}
    </>
  )
}

function Report({
  stats,
  days,
  onDays,
}: {
  stats: WebsiteStats
  days: WebsiteDays
  onDays: (days: WebsiteDays) => void
}) {
  const visitors = stats.totals.visitors
  const step = (key: string) => stats.funnel.find((f) => f.key === key)?.count ?? 0

  return (
    <div className="sec" style={{ gap: 20 }}>
      <Tabs
        label="Period"
        tabs={PERIODS}
        value={`${days}`}
        onChange={(key) => onDays(Number(key) as WebsiteDays)}
      />
      <div className="kpis">
        <KpiTile
          label="Visitors"
          value={numberFmt.format(visitors)}
          compare={`${numberFmt.format(stats.totals.visits)} visits`}
          hint="Each visitor counts once a day, so someone who comes back on three days counts three times."
        />
        <KpiTile label="Page views" value={numberFmt.format(stats.totals.pageviews)} compare="pages read" />
        <KpiTile
          label="Free audits started"
          value={numberFmt.format(step('free_audit_start'))}
          compare={`${percent(step('free_audit_start'), visitors)} of visitors`}
        />
        <KpiTile
          label="Accounts created"
          value={numberFmt.format(step('accounts'))}
          compare={`${numberFmt.format(stats.accounts_from_free_audit)} from the free audit`}
        />
      </div>

      <Panel title="Visitors and page views" sub="Per day, Manila time">
        <TimeChart
          title="Visitors and page views per day"
          kind="bar"
          bucket="day"
          points={stats.series}
          series={[
            { key: 'visitors', label: 'Visitors', color: 'var(--viz-1)' },
            { key: 'pageviews', label: 'Page views', color: 'var(--viz-2)' },
          ]}
        />
      </Panel>

      <div className="split">
        <Panel title="From visit to account" sub="Visitors who took each step, and the share of all visitors">
          <ol className="grid gap-3">
            {stats.funnel.map((f) => (
              <li
                key={f.key}
                className="grid grid-cols-[minmax(0,1fr)_64px_48px] items-center gap-3 text-[13px]"
              >
                <span className="grid gap-1.5">
                  <span className="font-medium text-ink">{f.label}</span>
                  <Track ratio={visitors > 0 ? Math.min(1, f.count / visitors) : 0} />
                </span>
                <span className="text-right font-semibold tabular-nums">{numberFmt.format(f.count)}</span>
                <span className="text-right text-ink-muted tabular-nums">{percent(f.count, visitors)}</span>
              </li>
            ))}
          </ol>
          <p className="mt-3 text-xs text-ink-subtle">
            Accounts come from the app’s sign-ups (the team’s own left out); the other steps from the website.
          </p>
        </Panel>
        <Panel title="Where visits come from" sub="The first page of each visit">
          <Bars
            rows={stats.channels.map((c) => ({ label: c.channel, value: c.visits }))}
            empty="No visits yet."
          />
        </Panel>
      </div>

      <div className="split">
        <Panel title="Pages" sub="Most read first">
          <Table
            caption="Pages, most read first"
            head={['Page', 'Views', 'Visitors']}
            rows={stats.pages.map((p) => [p.path, numberFmt.format(p.views), numberFmt.format(p.visitors)])}
            empty="No page views yet."
          />
        </Panel>
        <Panel title="What visitors did" sub="Buttons and links that matter">
          <Table
            caption="Actions on the website"
            head={['Action', 'Times', 'Visitors']}
            rows={stats.actions.map((a) => [
              ACTIONS[a.name] ?? a.name,
              numberFmt.format(a.count),
              numberFmt.format(a.visitors),
            ])}
            empty="No clicks counted yet."
          />
        </Panel>
      </div>

      <div className="split">
        <Panel title="Campaigns" sub="Tagged links: our ads, emails and posts">
          <Table
            caption="Campaigns"
            head={['Campaign', 'Source / medium', 'Visits']}
            rows={stats.campaigns.map((c) => [
              c.campaign,
              [c.source, c.medium].filter(Boolean).join(' / ') || '–',
              numberFmt.format(c.visits),
            ])}
            empty="No tagged visits yet."
          />
        </Panel>
        <Panel
          title="Websites that sent visitors"
          sub="Search engines, AI assistants, social media and links"
        >
          <Table
            caption="Websites that sent visitors"
            head={['Website', 'Visits']}
            rows={stats.referrers.map((r) => [r.host, numberFmt.format(r.visits)])}
            empty="No visits from other websites yet."
          />
        </Panel>
      </div>

      <div className="split">
        <Panel title="Countries" sub="From the visitor’s time zone">
          <Bars
            rows={stats.countries.map((c) => ({ label: countryName(c.value), value: c.visitors }))}
            empty="No visitors yet."
          />
        </Panel>
        <Panel title="Devices and browsers" sub="Visitors">
          <Bars
            rows={stats.devices.map((d) => ({ label: d.value, value: d.visitors }))}
            empty="No visitors yet."
          />
          <div className="mt-4">
            <Bars rows={stats.browsers.map((b) => ({ label: b.value, value: b.visitors }))} empty="" />
          </div>
        </Panel>
      </div>

      <Note calm title="Leave your own visits out">
        Your own visits count too. To leave a browser out, open arvaseo.com/?no-stats in it once
        (arvaseo.com/?stats counts it again). Counts are kept for {Math.round(stats.keep_days / 30)} months.
        The blog’s visits are in Cloudflare Web Analytics.
      </Note>
    </div>
  )
}

function Bars({ rows, empty }: { rows: { label: string; value: number }[]; empty: string }) {
  if (rows.length === 0) return empty ? <p className="empty-line">{empty}</p> : null
  const top = Math.max(...rows.map((r) => r.value), 1)

  return (
    <ul className="grid gap-2.5">
      {rows.map((r) => (
        <li
          key={r.label}
          className="grid grid-cols-[minmax(0,140px)_1fr_48px] items-center gap-3 text-[13px]"
        >
          <span className="truncate capitalize">{r.label}</span>
          <Track ratio={r.value / top} />
          <span className="text-right tabular-nums">{numberFmt.format(r.value)}</span>
        </li>
      ))}
    </ul>
  )
}

function Table({
  caption,
  head,
  rows,
  empty,
}: {
  caption: string
  head: string[]
  rows: string[][]
  empty: string
}) {
  if (rows.length === 0) return <p className="empty-line">{empty}</p>

  return (
    <div className="tw" style={{ border: 0 }}>
      <table className="t">
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr>
            {head.map((h, i) => (
              <th key={h} className={i > 0 && i === head.length - 1 ? 'r' : undefined}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.join('|')}>
              {row.map((cell, i) => (
                <td
                  key={i}
                  className={i === 0 ? undefined : i === row.length - 1 ? 'r num' : 'm'}
                  style={i === 0 ? { wordBreak: 'break-all' } : undefined}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
